import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatKES } from "../lib/api";
import { ArrowRight, ShieldCheck, MessageCircle, Wallet, Star, Hammer, Sparkles, Trees, Lock, CheckCircle2, Home, Baby, Shield, Wrench, Car, ChefHat, Briefcase, Dog, GraduationCap, HeartPulse, Brush, Users } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/8d6828f3-9d76-4942-a0a1-4943df0b0327/images/9805a29fdcfc6e2cfb5907ef022d363bf4d349477b3fc33d70a6fb027508bc92.png";
const NANNY_IMG = "https://static.prod-images.emergentagent.com/jobs/8d6828f3-9d76-4942-a0a1-4943df0b0327/images/311a2ea8b2934951f8dcbed014e760f93afd66bf4cf0dc29d722733f28f6bb95.png";
const SEC_IMG = "https://static.prod-images.emergentagent.com/jobs/8d6828f3-9d76-4942-a0a1-4943df0b0327/images/3f56d9b8a6d2ab288491333863cbb000950340bec84d91bbd648887d57a59837.png";
const TRADES_IMG = "https://static.prod-images.emergentagent.com/jobs/8d6828f3-9d76-4942-a0a1-4943df0b0327/images/abde83ca6ecdbd91c7555994085427dcb9a81886d77e80267a9fc0e2d1100bef.png";
const GARDEN_IMG = "https://images.unsplash.com/photo-1686663048931-6df69f577a2f?w=900&q=80";

// Icon map for category tiles — falls back to Users icon if slug not mapped.
const CATEGORY_ICONS = {
  "househelp": Home,
  "house-manager": Briefcase,
  "nanny": Baby,
  "gardener": Trees,
  "security": Shield,
  "handyman": Wrench,
  "carpenter": Hammer,
  "mason": Hammer,
  "electrician": Sparkles,
  "plumber": Wrench,
  "landscaper": Trees,
  "painter": Brush,
  "tile-layer": Hammer,
  "casual": Users,
  // extras for future categories
  "driver": Car,
  "chef": ChefHat,
  "cook": ChefHat,
  "cleaner": Brush,
  "petcare": Dog,
  "tutor": GraduationCap,
  "caregiver": HeartPulse,
};

export default function Landing() {
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/tiers", { params: { audience: "customer" } }).then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />

      {/* HERO - asymmetric */}
      <section className="relative pt-12 lg:pt-20 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="label-eyebrow mb-6">Efficient placement at the palm of your hands</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]">
              Find the right fit<br />
              <span className="text-[#1E3A8A]">at the right time.</span><br />
              Run your home or business seamlessly.
            </h1>
            <p className="text-lg text-[#5B6478] mt-8 max-w-xl leading-relaxed">
              Keritex Staffing Bureau connects Kenyan families and businesses with vetted househelps,
              nannies, gardeners, security, and skilled tradesmen — all through one private platform.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                asChild
                data-testid="hero-browse-btn"
                className="bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE] rounded-md h-12 px-6 text-base"
              >
                <Link to="/browse">Browse Staff <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button
                asChild
                data-testid="hero-employer-btn"
                className="bg-[#7C3AED] hover:bg-[#5B21B6] text-white rounded-md h-12 px-6 text-base"
              >
                <Link to="/signup?role=customer">I'm an Employer</Link>
              </Button>
              <Button
                asChild
                data-testid="hero-join-btn"
                variant="outline"
                className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-[#F8F9FE] rounded-md h-12 px-6 text-base"
              >
                <Link to="/signup?role=employee">Join as Staff</Link>
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-[#5B6478]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1E3A8A]" /> Verified profiles</div>
              <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#1E3A8A]" /> Private contact</div>
              <div className="flex items-center gap-2"><Wallet className="w-4 h-4 text-[#1E3A8A]" /> M-Pesa payroll</div>
            </div>
          </div>

          {/* Tetris image grid */}
          <div className="lg:col-span-5 grid grid-cols-6 gap-3 lg:gap-4">
            <div className="col-span-4 row-span-2 aspect-[3/4] rounded-md overflow-hidden bg-[#EEF0FA]">
              <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 aspect-square rounded-md overflow-hidden bg-[#EEF0FA]">
              <img src={NANNY_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-2 aspect-square rounded-md overflow-hidden bg-[#1E3A8A] text-[#F8F9FE] p-4 flex flex-col justify-between">
              <div className="label-eyebrow text-[#F8F9FE]/70">Hires</div>
              <div>
                <div className="font-display font-bold text-3xl">2,400+</div>
                <div className="text-xs opacity-80 mt-1">successful placements</div>
              </div>
            </div>
            <div className="col-span-3 aspect-[4/3] rounded-md overflow-hidden bg-[#EEF0FA]">
              <img src={SEC_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="col-span-3 aspect-[4/3] rounded-md overflow-hidden bg-[#EEF0FA]">
              <img src={TRADES_IMG} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 bg-[#EEF0FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="label-eyebrow mb-3">Categories</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-2xl">
                Every role for the modern Kenyan home and business
              </h2>
            </div>
            <Link to="/browse" data-testid="see-all-categories" className="hidden md:flex items-center gap-2 text-sm font-semibold hover:text-[#1E3A8A]">
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5">
            {categories.map((c) => {
              const Icon = CATEGORY_ICONS[c.slug?.toLowerCase()] || Users;
              return (
                <Link
                  key={c.slug}
                  to={`/browse?category=${c.slug}`}
                  data-testid={`cat-${c.slug}`}
                  className="cat-tile-3d group"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="cat-icon shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="font-display font-semibold truncate text-sm sm:text-base">{c.name}</span>
                  </div>
                  <ArrowRight className="cat-arrow w-4 h-4 shrink-0 hidden sm:block" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-3">How it works</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-tight">
              From browsing to payroll, in one place.
            </h2>
            <p className="text-[#5B6478] mt-6 leading-relaxed">
              We handle the vetting, the introductions, and the money. You just hire the right person.
            </p>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {[
              { i: "01", t: "Browse a vetted pool", d: "Filter by category, location, experience and rating. Contact details stay private until you connect.", icon: Sparkles },
              { i: "02", t: "Request through chat or ticket", d: "Open an anonymized chat with the staff member, or submit a ticket for the KSB team to match you.", icon: MessageCircle },
              { i: "03", t: "Place and manage", d: "Confirm the hire, set the role and salary. Track placements in your dashboard.", icon: ShieldCheck },
              { i: "04", t: "Pay via M-Pesa Payroll", d: "Run salary payments directly through KSB. Receipts, history and discipline records — all in one log.", icon: Wallet },
            ].map((s) => (
              <div key={s.i} className="grid grid-cols-12 gap-4 items-center bg-white border border-[#E4E7F5] rounded-md p-6 hover:shadow-sm transition-shadow">
                <div className="col-span-2 font-display text-3xl font-bold text-[#7C3AED]">{s.i}</div>
                <div className="col-span-8">
                  <h3 className="font-display font-bold text-lg">{s.t}</h3>
                  <p className="text-sm text-[#5B6478] mt-1 leading-relaxed">{s.d}</p>
                </div>
                <div className="col-span-2 flex justify-end">
                  <s.icon className="w-6 h-6 text-[#1E3A8A]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24 bg-[#0F1E4B] text-[#F8F9FE]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="label-eyebrow mb-3 text-[#F8F9FE]/60">Pricing</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Simple, transparent. In shillings.</h2>
            <p className="text-[#F8F9FE]/70 mt-4 max-w-2xl mx-auto">
              Flat monthly plans for power users. Or pay once, hire once.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {tiers.map((t, i) => (
              <div
                key={t.id}
                data-testid={`tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`price-tile price-tile-dark flex flex-col ${i === 1 ? "price-tile-popular" : ""}`}
              >
                {i === 1 && <span className="price-tile-ribbon">Popular</span>}
                <div className="font-display font-bold text-2xl">{t.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-extrabold">{formatKES(t.price)}</span>
                  <span className="text-sm opacity-70">/{t.period === "monthly" ? "mo" : "once"}</span>
                </div>
                <ul className="mt-6 space-y-3 text-sm flex-1">
                  {t.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  data-testid={`tier-cta-${t.id}`}
                  className="mt-7 rounded-md bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
                >
                  <Link to="/signup">Get {t.name}</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS / TRUST */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="label-eyebrow mb-3">Why families trust us</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-tight">
              Quality, discipline, professionalism.
            </h2>
            <p className="text-[#5B6478] mt-6 leading-relaxed">
              KSB staff are vetted, background-aware, and stay on the platform — so you have recourse,
              records, and reviews that build accountability.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border-l-2 border-[#7C3AED] pl-4">
                <div className="font-display text-4xl font-extrabold text-[#1E3A8A]">4.8★</div>
                <div className="text-sm text-[#5B6478] mt-1">Avg. staff rating</div>
              </div>
              <div className="border-l-2 border-[#7C3AED] pl-4">
                <div className="font-display text-4xl font-extrabold text-[#1E3A8A]">98%</div>
                <div className="text-sm text-[#5B6478] mt-1">Disputes resolved</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { q: "Found a nanny within a week. Grace has been a blessing for our family.", a: "Wanjiru K., Westlands" },
              { q: "Our entire household payroll moved to KSB. So much easier than chasing M-Pesa receipts.", a: "Mwangi D., Karen" },
              { q: "As a security guard, getting on KSB doubled my income. Premium tier worth every shilling.", a: "John K., Kileleshwa" },
              { q: "Their tradesmen come on time. Rare and refreshing.", a: "Aisha O., Lavington" },
            ].map((t, i) => (
              <div key={i} className="bg-white border border-[#E4E7F5] rounded-md p-6">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#7C3AED] text-[#7C3AED]" />)}
                </div>
                <p className="font-display text-lg leading-snug">"{t.q}"</p>
                <p className="text-xs text-[#5B6478] mt-4 uppercase tracking-widest font-semibold">— {t.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-md overflow-hidden bg-[#1E3A8A] text-[#F8F9FE] p-10 lg:p-16">
            <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
                  Build a household that runs itself.
                </h2>
                <p className="mt-4 text-[#F8F9FE]/80 max-w-xl">
                  Sign up free. Browse the pool. Hire when you're ready.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Button
                  asChild
                  data-testid="cta-final-signup"
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-md h-14 px-8 text-base"
                >
                  <Link to="/signup">Start free <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
              </div>
            </div>
            <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-[#7C3AED]/20 blur-3xl" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
